class FaqSection extends HTMLElement {
  static mediaQueryLarge = matchMedia('(min-width: 990px)');
  static reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  static isMobileBreakpoint() {
    return !FaqSection.mediaQueryLarge.matches;
  }

  static prefersReducedMotion() {
    return FaqSection.reducedMotion.matches;
  }

  #controller = new AbortController();
  #observer = null;
  #sections = [];
  #links = [];
  #activeSection = null;
  #scrollTimeout = null;
  #isScrolling = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#buildTOC();

    this.#links = [...this.querySelectorAll('[data-faq-anchor]')];
    this.#sections = [...this.querySelectorAll('[data-faq-section]')];

    if (this.#links.length === 0 || this.#sections.length === 0) {
      return;
    }

    this.#links.forEach((link) => {
      link.addEventListener('click', this.#handleClick, { signal });
    });

    this.#setupScrollSpy();

    FaqSection.mediaQueryLarge.addEventListener('change', this.#handleMediaChange, { signal });

    this.#setInitialActiveState();
  }

  #buildTOC() {
    const tocList = this.querySelector('[data-toc-list]');
    if (!tocList) return;

    const faqGroups = [...this.querySelectorAll('[data-faq-section]')];

    faqGroups.forEach((group) => {
      const groupTitle = group.querySelector('.faq-group__title');
      if (groupTitle && group.id) {
        const groupItem = document.createElement('li');
        groupItem.className = 'faq-toc__item faq-toc__item--group';

        const link = document.createElement('a');
        link.href = `#${group.id}`;
        link.className = 'faq-toc__link';
        link.setAttribute('data-faq-anchor', '');
        link.textContent = groupTitle.textContent.trim();

        groupItem.appendChild(link);
        tocList.appendChild(groupItem);
      }
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
    }
  }

  #handleClick = (event) => {
    event.preventDefault();

    const link = event.currentTarget;
    const targetId = link.getAttribute('href')?.slice(1);

    if (!targetId) return;

    const targetSection = document.getElementById(targetId);
    if (!targetSection) return;

    this.#setActiveLink(link);

    const offset = this.#getScrollOffset();
    const targetPosition = targetSection.getBoundingClientRect().top + window.scrollY - offset;

    this.#isScrolling = true;
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
    }

    window.scrollTo({
      top: targetPosition,
      behavior: FaqSection.prefersReducedMotion() ? 'auto' : 'smooth',
    });

    this.#scrollTimeout = setTimeout(() => {
      this.#isScrolling = false;
    }, 800);

    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', `#${targetId}`);
    }
  };

  #setupScrollSpy() {
    this.#observer?.disconnect();

    const isMobile = FaqSection.isMobileBreakpoint();
    const offset = this.#getScrollOffset();
    const topMargin = isMobile ? `-${offset + 20}px` : '-120px';

    const options = {
      root: null,
      rootMargin: `${topMargin} 0px -70% 0px`,
      threshold: [0, 0.05, 0.1, 0.25],
    };

    this.#observer = new IntersectionObserver((entries) => {
      if (this.#isScrolling) return;

      if (this.#scrollTimeout) {
        clearTimeout(this.#scrollTimeout);
      }

      this.#scrollTimeout = setTimeout(() => {
        const intersectingSections = [];
        const viewportMiddle = window.innerHeight / 2;

        this.#sections.forEach((section) => {
          const rect = section.getBoundingClientRect();

          if (rect.bottom > 0 && rect.top < window.innerHeight) {
            const sectionMiddle = rect.top + (rect.height / 2);
            const distanceFromCenter = Math.abs(sectionMiddle - viewportMiddle);

            intersectingSections.push({
              section,
              distanceFromCenter,
              rect
            });
          }
        });

        // Sort by which one is closest to viewport center
        intersectingSections.sort((a, b) => a.distanceFromCenter - b.distanceFromCenter);

        if (intersectingSections.length > 0) {
          const sectionId = intersectingSections[0].section.id;

          // Only update if this is a different section
          if (this.#activeSection !== sectionId) {
            this.#activeSection = sectionId;
            const activeLink = this.querySelector(`[href="#${sectionId}"]`);

            if (activeLink) {
              this.#setActiveLink(activeLink);
            }
          }
        }
      }, 50); // 50ms debounce
    }, options);

    // Observe all sections
    this.#sections.forEach((section) => {
      this.#observer?.observe(section);
    });
  }

  #setActiveLink(activeLink) {
    this.#links.forEach((link) => {
      link.classList.remove('faq-toc__link--active');
      link.setAttribute('aria-current', 'false');
    });

    activeLink.classList.add('faq-toc__link--active');
    activeLink.setAttribute('aria-current', 'true');

    // On mobile, scroll the TOC to show the active link
    if (FaqSection.isMobileBreakpoint()) {
      this.#scrollTocToActiveLink(activeLink);
    }
  }

  #scrollTocToActiveLink(activeLink) {
    const tocContainer = this.querySelector('.faq-toc');

    if (!tocContainer || !(activeLink instanceof HTMLElement)) return;

    const linkLeft = activeLink.offsetLeft;
    const containerPadding = 16; // Account for padding-inline

    // Scroll to align active link to the left (with padding offset)
    tocContainer.scrollTo({
      left: linkLeft - containerPadding,
      behavior: FaqSection.prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #getScrollOffset() {
    const isMobile = FaqSection.isMobileBreakpoint();

    if (isMobile) {
      // On mobile, account for sticky TOC at top
      const tocElement = this.querySelector('.faq-toc--sticky');
      const tocHeight = tocElement ? tocElement.offsetHeight : 0;

      // Also check for theme header
      const header = document.querySelector('.header, [data-header], .shopify-section-header');
      const headerHeight = header ? header.offsetHeight : 0;

      // Add extra spacing
      return tocHeight + headerHeight + 20;
    }

    // On desktop, account for any sticky header (TOC is on the side, not overlapping)
    const header = document.querySelector('.header, [data-header], .shopify-section-header');
    const headerHeight = header ? header.offsetHeight : 0;

    return headerHeight + 40;
  }

  #setInitialActiveState() {
    const hash = window.location.hash;

    if (hash) {
      const targetLink = this.querySelector(`[href="${hash}"]`);
      const targetSection = document.getElementById(hash.slice(1));

      if (targetLink instanceof HTMLElement) {
        this.#setActiveLink(targetLink);

        if (targetSection) {
          setTimeout(() => {
            const offset = this.#getScrollOffset();
            const targetPosition = targetSection.getBoundingClientRect().top + window.scrollY - offset;

            window.scrollTo({
              top: targetPosition,
              behavior: FaqSection.prefersReducedMotion() ? 'auto' : 'smooth',
            });
          }, 100);
        }
        return;
      }
    }

    if (this.#links.length > 0) {
      this.#setActiveLink(this.#links[0]);
    }
  }

  #handleMediaChange = () => {
    // Re-setup scroll spy with new viewport dimensions
    this.#setupScrollSpy();
  };
}

// Register custom element
if (!customElements.get('faq-section')) {
  customElements.define('faq-section', FaqSection);
}
